import styled, { keyframes } from "styled-components";

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${({ theme }) =>
    theme.body === "#fafbfc"
      ? "rgba(0, 0, 0, 0.4)"
      : "rgba(0, 0, 0, 0.6)"};
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  animation: ${fadeIn} 0.2s ease;
`;

export const ModalContainer = styled.div`
  width: 90%;
  max-width: 600px;
  font-size: 1em;
  background: ${({ theme }) =>
    theme.body === "#fafbfc"
      ? "linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(250, 250, 252, 0.98) 100%)"
      : "linear-gradient(135deg, rgba(20, 20, 25, 0.95) 0%, rgba(15, 15, 20, 0.98) 100%)"};
  backdrop-filter: blur(20px);
  border-radius: 14px;
  border: 1px solid
    ${({ theme }) =>
      theme.body === "#fafbfc"
        ? "rgba(0, 0, 0, 0.1)"
        : "rgba(255, 255, 255, 0.08)"};
  box-shadow: ${({ theme }) =>
    theme.body === "#fafbfc"
      ? "0 20px 60px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05) inset"
      : "0 20px 60px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.05) inset"};
  overflow: hidden;
  animation: ${slideUp} 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  display: flex;
  flex-direction: column;
  max-height: 90vh;
`;

export const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.2em 1.4em;
  border-bottom: 1px solid
    ${({ theme }) =>
      theme.body === "#fafbfc"
        ? "rgba(0, 0, 0, 0.08)"
        : "rgba(255, 255, 255, 0.06)"};
  background: ${({ theme }) =>
    theme.body === "#fafbfc"
      ? "rgba(255, 255, 255, 0.5)"
      : "rgba(255, 255, 255, 0.02)"};
`;

export const ModalTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: 0.5em;
  font-size: 1em;
  font-weight: 600;
  color: ${({ theme }) => theme.text};
  margin: 0;

  svg {
    font-size: 1em;
    color: ${({ theme }) => theme.primary};
  }
`;

export const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.8em;
  height: 1.8em;
  background: ${({ theme }) =>
    theme.body === "#fafbfc"
      ? "rgba(0, 0, 0, 0.05)"
      : "rgba(255, 255, 255, 0.06)"};
  border: 1px solid
    ${({ theme }) =>
      theme.body === "#fafbfc"
        ? "rgba(0, 0, 0, 0.08)"
        : "rgba(255, 255, 255, 0.08)"};
  border-radius: 7px;
  color: ${({ theme }) => theme.text};
  cursor: pointer;
  transition: all 0.2s ease;

  svg {
    font-size: 1.1em;
  }

  &:hover {
    background: ${({ theme }) =>
      theme.body === "#fafbfc"
        ? "rgba(0, 0, 0, 0.08)"
        : "rgba(255, 255, 255, 0.1)"};
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

export const TabsContainer = styled.div`
  display: flex;
  gap: 0;
  padding: 0 1.4em;
  background: ${({ theme }) =>
    theme.body === "#fafbfc"
      ? "rgba(255, 255, 255, 0.5)"
      : "rgba(255, 255, 255, 0.02)"};
  border-bottom: 1px solid
    ${({ theme }) =>
      theme.body === "#fafbfc"
        ? "rgba(0, 0, 0, 0.08)"
        : "rgba(255, 255, 255, 0.06)"};
`;

export const Tab = styled.button`
  flex: 1;
  padding: 0.9em 1.2em;
  background: transparent;
  border: none;
  border-bottom: 2px solid ${({ $active, theme }) => $active ? theme.primary : 'transparent'};
  color: ${({ $active, theme }) => $active ? theme.primary : theme.text};
  opacity: ${({ $active }) => $active ? 1 : 0.6};
  font-size: 0.85em;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5em;

  svg {
    font-size: 1em;
  }

  &:hover {
    opacity: 1;
    background: ${({ theme }) =>
      theme.body === "#fafbfc"
        ? "rgba(0, 0, 0, 0.03)"
        : "rgba(255, 255, 255, 0.03)"};
  }
`;

export const ModalBody = styled.div`
  padding: 1.4em;
  overflow-y: auto;
  flex: 1;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) =>
      theme.body === "#fafbfc"
        ? "rgba(0, 0, 0, 0.03)"
        : "rgba(255, 255, 255, 0.03)"};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) =>
      theme.body === "#fafbfc"
        ? "rgba(0, 0, 0, 0.15)"
        : "rgba(255, 255, 255, 0.15)"};
    border-radius: 3px;

    &:hover {
      background: ${({ theme }) =>
        theme.body === "#fafbfc"
          ? "rgba(0, 0, 0, 0.25)"
          : "rgba(255, 255, 255, 0.25)"};
    }
  }
`;

export const InfoBox = styled.div`
  background: ${({ theme }) =>
    theme.body === "#fafbfc"
      ? "rgba(16, 185, 129, 0.05)"
      : "rgba(16, 185, 129, 0.08)"};
  border: 1px solid
    ${({ theme }) =>
      theme.body === "#fafbfc"
        ? "rgba(16, 185, 129, 0.15)"
        : "rgba(16, 185, 129, 0.2)"};
  border-radius: 8px;
  padding: 0.7em 1em;
  margin-bottom: 1em;
  display: flex;
  align-items: flex-start;
  gap: 0.6em;

  svg {
    color: ${({ theme }) => theme.primary};
    font-size: 1em;
    flex-shrink: 0;
    margin-top: 0.1em;
  }

  .info-content {
    flex: 1;

    h4 {
      margin: 0 0 0.3em 0;
      font-size: 0.85em;
      font-weight: 600;
      color: ${({ theme }) => theme.text};
    }

    p {
      margin: 0;
      font-size: 0.75em;
      line-height: 1.4;
      color: ${({ theme }) => theme.text};
      opacity: 0.8;
    }

    ul {
      margin: 0.4em 0 0 0;
      padding-left: 1.2em;
      font-size: 0.75em;
      line-height: 1.5;
      color: ${({ theme }) => theme.text};
      opacity: 0.8;

      li {
        margin: 0.3em 0;
      }
    }
  }
`;

export const Dropzone = styled.div`
  border: 2px dashed
    ${({ theme }) =>
      theme.body === "#fafbfc"
        ? "rgba(0, 0, 0, 0.15)"
        : "rgba(255, 255, 255, 0.15)"};
  border-radius: 10px;
  background: ${({ theme }) =>
    theme.body === "#fafbfc"
      ? "rgba(0, 0, 0, 0.02)"
      : "rgba(255, 255, 255, 0.02)"};
  padding: 2.5em 1.5em;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 1.2em;

  &:hover {
    border-color: ${({ theme }) => theme.primary};
    background: ${({ theme }) =>
      theme.body === "#fafbfc"
        ? "rgba(16, 185, 129, 0.03)"
        : "rgba(16, 185, 129, 0.05)"};
  }

  &.active {
    border-color: ${({ theme }) => theme.primary};
    background: ${({ theme }) =>
      theme.body === "#fafbfc"
        ? "rgba(16, 185, 129, 0.08)"
        : "rgba(16, 185, 129, 0.12)"};
  }

  .upload-icon {
    font-size: 2.5em;
    color: ${({ theme }) => theme.primary};
    margin-bottom: 0.8em;
    opacity: 0.7;
  }

  h4 {
    margin: 0 0 0.5em 0;
    font-size: 0.95em;
    font-weight: 600;
    color: ${({ theme }) => theme.text};
  }

  p {
    margin: 0 0 0.8em 0;
    font-size: 0.8em;
    color: ${({ theme }) => theme.text};
    opacity: 0.6;
  }

  .file-types {
    display: inline-block;
    padding: 0.4em 0.8em;
    background: ${({ theme }) =>
      theme.body === "#fafbfc"
        ? "rgba(0, 0, 0, 0.05)"
        : "rgba(255, 255, 255, 0.06)"};
    border: 1px solid
      ${({ theme }) =>
        theme.body === "#fafbfc"
          ? "rgba(0, 0, 0, 0.1)"
          : "rgba(255, 255, 255, 0.1)"};
    border-radius: 5px;
    font-size: 0.75em;
    color: ${({ theme }) => theme.text};
    opacity: 0.7;
    font-weight: 500;
  }
`;

export const FileList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6em;
  margin-bottom: 1.2em;
`;

export const FileItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8em;
  padding: 0.8em 1em;
  background: ${({ theme }) =>
    theme.body === "#fafbfc"
      ? "rgba(0, 0, 0, 0.03)"
      : "rgba(255, 255, 255, 0.04)"};
  border: 1px solid
    ${({ theme }) =>
      theme.body === "#fafbfc"
        ? "rgba(0, 0, 0, 0.08)"
        : "rgba(255, 255, 255, 0.08)"};
  border-radius: 8px;
  font-size: 0.85em;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) =>
      theme.body === "#fafbfc"
        ? "rgba(0, 0, 0, 0.05)"
        : "rgba(255, 255, 255, 0.06)"};
  }

  svg {
    color: ${({ theme }) => theme.primary};
    font-size: 1.3em;
    flex-shrink: 0;
  }

  .file-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.3em;
    min-width: 0;

    .file-name {
      color: ${({ theme }) => theme.text};
      font-weight: 500;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .file-size {
      color: ${({ theme }) => theme.text};
      opacity: 0.5;
      font-size: 0.85em;
    }
  }

  button {
    background: transparent;
    border: none;
    color: #ef4444;
    cursor: pointer;
    padding: 0.3em;
    display: flex;
    align-items: center;
    opacity: 0.6;
    transition: all 0.2s ease;
    border-radius: 4px;

    svg {
      font-size: 1.1em;
      color: #ef4444;
    }

    &:hover {
      opacity: 1;
      background: rgba(239, 68, 68, 0.1);
    }
  }
`;

export const SubmitButton = styled.button`
  width: 100%;
  padding: 0.8em 1.5em;
  background: ${({ disabled, theme }) =>
    disabled ? (theme.body === "#fafbfc" ? "rgba(0, 0, 0, 0.1)" : "rgba(255, 255, 255, 0.1)") : theme.primary};
  color: ${({ disabled, theme }) => disabled ? (theme.body === "#fafbfc" ? "rgba(0, 0, 0, 0.3)" : "rgba(255, 255, 255, 0.3)") : "white"};
  border: none;
  border-radius: 8px;
  font-size: 0.9em;
  font-weight: 600;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6em;
  box-shadow: ${({ disabled }) => disabled ? "none" : "0 2px 8px rgba(16, 185, 129, 0.25)"};

  svg {
    font-size: 1.1em;
  }

  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.35);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
  }
`;

export const FilterButton = styled.button`
  width: 100%;
  padding: 1em 1.2em;
  background: ${({ theme }) =>
    theme.body === "#fafbfc"
      ? "rgba(0, 0, 0, 0.03)"
      : "rgba(255, 255, 255, 0.04)"};
  border: 1px solid
    ${({ theme }) =>
      theme.body === "#fafbfc"
        ? "rgba(0, 0, 0, 0.1)"
        : "rgba(255, 255, 255, 0.1)"};
  border-radius: 8px;
  color: ${({ theme }) => theme.text};
  font-size: 0.9em;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.2em;

  .filter-label {
    display: flex;
    align-items: center;
    gap: 0.7em;

    svg {
      color: ${({ theme }) => theme.primary};
      font-size: 1.2em;
    }
  }

  .filter-value {
    font-size: 0.8em;
    opacity: 0.6;
  }

  &:hover {
    background: ${({ theme }) =>
      theme.body === "#fafbfc"
        ? "rgba(0, 0, 0, 0.05)"
        : "rgba(255, 255, 255, 0.06)"};
    border-color: ${({ theme }) => theme.primary};
  }

  &:active {
    transform: scale(0.99);
  }
`;

export const FilterGroup = styled.div`
  margin-bottom: 1.2em;
  position: relative;
`;

export const FilterLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5em;
  font-size: 0.85em;
  font-weight: 500;
  color: ${({ theme }) => theme.text};
  margin-bottom: 0.5em;
  
  svg {
    color: ${({ theme }) => theme.primary};
  }
`;

export const FilterBadges = styled.div`
  display: flex;
  gap: 0.5em;
  flex-wrap: wrap;
`;

export const FilterBadge = styled.button`
  background: ${({ $active, theme }) => $active ? theme.primary : (theme.body === "#fafbfc" ? "rgba(0, 0, 0, 0.05)" : "rgba(255, 255, 255, 0.05)")};
  color: ${({ $active, theme }) => $active ? "white" : theme.text};
  border: 1px solid ${({ $active, theme }) => $active ? theme.primary : (theme.body === "#fafbfc" ? "rgba(0, 0, 0, 0.05)" : "rgba(255, 255, 255, 0.1)")};
  padding: 0.4em 0.8em;
  border-radius: 6px;
  font-size: 0.85em;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${({ $active, theme }) => $active ? theme.primary : (theme.body === "#fafbfc" ? "rgba(0, 0, 0, 0.08)" : "rgba(255, 255, 255, 0.1)")};
  }
`;

export const FilterSectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5em;
  font-size: 0.9em;
  font-weight: 600;
  color: ${({ theme }) => theme.text};
  margin-bottom: 0.8em;
  padding-bottom: 0.5em;
  border-bottom: 1px solid ${({ theme }) => theme.body === "#fafbfc" ? "rgba(0, 0, 0, 0.05)" : "rgba(255, 255, 255, 0.05)"};
  
  svg {
    color: ${({ theme }) => theme.primary};
  }
`;

export const FilterInput = styled.input`
  width: 100%;
  padding: 0.7em 0.9em;
  background: ${({ theme }) => theme.body === "#fafbfc" ? "rgba(0, 0, 0, 0.03)" : "rgba(255, 255, 255, 0.04)"};
  border: 1px solid ${({ theme }) => theme.body === "#fafbfc" ? "rgba(0, 0, 0, 0.1)" : "rgba(255, 255, 255, 0.1)"};
  border-radius: 6px;
  font-size: 0.9em;
  color: ${({ theme }) => theme.text};
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.primary};
    background: ${({ theme }) => theme.body === "#fafbfc" ? "rgba(0, 0, 0, 0.02)" : "rgba(255, 255, 255, 0.06)"};
  }
`;

export const SuggestionsContainer = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: ${({ theme }) => theme.body === "#fafbfc" ? "rgba(255, 255, 255, 0.98)" : "rgba(20, 20, 25, 0.98)"};
  border: 1px solid ${({ theme }) => theme.body === "#fafbfc" ? "rgba(0, 0, 0, 0.1)" : "rgba(255, 255, 255, 0.1)"};
  border-radius: 8px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 10;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  margin-top: 4px;
`;

export const SuggestionItem = styled.div`
  padding: 0.8em;
  display: flex;
  align-items: center;
  gap: 0.8em;
  cursor: pointer;
  border-bottom: 1px solid ${({ theme }) => theme.body === "#fafbfc" ? "rgba(0, 0, 0, 0.05)" : "rgba(255, 255, 255, 0.05)"};
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background: ${({ theme }) => theme.body === "#fafbfc" ? "rgba(0, 0, 0, 0.05)" : "rgba(255, 255, 255, 0.05)"};
  }
  
  .suggestion-icon {
    width: 2em;
    height: 2em;
    background: ${({ theme }) => theme.body === "#fafbfc" ? "rgba(16, 185, 129, 0.1)" : "rgba(16, 185, 129, 0.15)"};
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${({ theme }) => theme.primary};
  }
  
  .suggestion-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    
    .suggestion-name {
      font-weight: 500;
      color: ${({ theme }) => theme.text};
      font-size: 0.9em;
    }
    
    .suggestion-cnpj {
      font-size: 0.8em;
      opacity: 0.6;
      color: ${({ theme }) => theme.text};
    }
  }
`;
